import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Search, ArrowLeft, AlertCircle, FileQuestion } from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/dashboard?search=${searchQuery}`);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
    },
    floatingCircle: {
      position: 'absolute',
      borderRadius: '50%',
      opacity: 0.1,
      animation: 'float 20s infinite ease-in-out',
    },
    circle1: {
      width: '300px',
      height: '300px',
      background: '#fff',
      top: '10%',
      left: '10%',
      animationDelay: '0s',
    },
    circle2: {
      width: '200px',
      height: '200px',
      background: '#fff',
      bottom: '20%',
      right: '15%',
      animationDelay: '5s',
    },
    circle3: {
      width: '150px',
      height: '150px',
      background: '#fff',
      top: '60%',
      left: '80%',
      animationDelay: '10s',
    },
    contentWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '24px',
      padding: '60px 40px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      maxWidth: '600px',
      width: '100%',
      textAlign: 'center',
      position: 'relative',
      zIndex: 1,
      animation: 'slideUp 0.6s ease-out',
    },
    iconWrapper: {
      marginBottom: '32px',
      position: 'relative',
      display: 'inline-block',
    },
    icon404: {
      fontSize: '120px',
      fontWeight: 'bold',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      lineHeight: 1,
      animation: 'pulse 2s infinite',
    },
    floatingIcon: {
      position: 'absolute',
      animation: 'bounce 2s infinite',
    },
    title: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '16px',
    },
    description: {
      fontSize: '18px',
      color: '#64748b',
      marginBottom: '32px',
      lineHeight: '1.6',
    },
    suggestionBox: {
      backgroundColor: '#f8fafc',
      padding: '20px',
      borderRadius: '12px',
      marginBottom: '32px',
      border: '1px solid #e2e8f0',
    },
    suggestionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    suggestionList: {
      textAlign: 'left',
      color: '#64748b',
      fontSize: '14px',
      lineHeight: '1.8',
    },
    suggestionItem: {
      marginBottom: '8px',
      paddingLeft: '24px',
      position: 'relative',
    },
    bullet: {
      position: 'absolute',
      left: '8px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '6px',
      height: '6px',
      backgroundColor: '#667eea',
      borderRadius: '50%',
    },
    buttonGroup: {
      display: 'flex',
      gap: '16px',
      justifyContent: 'center',
      flexWrap: 'wrap',
      marginBottom: '24px',
    },
    primaryButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '14px 28px',
      backgroundColor: '#667eea',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    },
    secondaryButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '14px 28px',
      backgroundColor: '#ffffff',
      color: '#667eea',
      border: '2px solid #667eea',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    countdown: {
      fontSize: '14px',
      color: '#64748b',
      marginTop: '16px',
      padding: '12px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      display: 'inline-block',
    },
    countdownNumber: {
      fontWeight: 'bold',
      color: '#667eea',
      fontSize: '18px',
    },
    searchBox: {
      marginTop: '32px',
      position: 'relative',
    },
    searchForm: {
      position: 'relative',
    },
    searchInput: {
      width: '100%',
      padding: '14px 44px 14px 16px',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '16px',
      outline: 'none',
      transition: 'border-color 0.3s ease',
      boxSizing: 'border-box',
    },
    searchButton: {
      position: 'absolute',
      right: '8px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  // Add CSS animations
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes pulse {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
        }
      }

      @keyframes bounce {
        0%, 100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-20px);
        }
      }

      @keyframes float {
        0%, 100% {
          transform: translate(0, 0) scale(1);
        }
        33% {
          transform: translate(30px, -30px) scale(1.1);
        }
        66% {
          transform: translate(-20px, 20px) scale(0.9);
        }
      }

      @media (max-width: 640px) {
        .icon-404 {
          fontSize: 80px !important;
        }
        .title-404 {
          fontSize: 24px !important;
        }
        .desc-404 {
          fontSize: 16px !important;
        }
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <div style={styles.container}>
      {/* Floating background circles */}
      <div style={{ ...styles.floatingCircle, ...styles.circle1 }} />
      <div style={{ ...styles.floatingCircle, ...styles.circle2 }} />
      <div style={{ ...styles.floatingCircle, ...styles.circle3 }} />

      <div style={styles.contentWrapper}>
        {/* 404 Icon */}
        <div style={styles.iconWrapper}>
          <div className="icon-404" style={styles.icon404}>404</div>
          <FileQuestion 
            size={48} 
            color="#667eea" 
            style={{ 
              ...styles.floatingIcon, 
              top: '-20px', 
              right: '-20px' 
            }} 
          />
        </div>

        {/* Title */}
        <h1 className="title-404" style={styles.title}>Oops! Page Not Found</h1>

        {/* Description */}
        <p className="desc-404" style={styles.description}>
          The page you're looking for seems to have wandered off. 
          Don't worry, even the best explorers get lost sometimes!
        </p>

        {/* Suggestion Box */}
        <div style={styles.suggestionBox}>
          <div style={styles.suggestionTitle}>
            <AlertCircle size={20} />
            What you can do:
          </div>
          <div style={styles.suggestionList}>
            <div style={styles.suggestionItem}>
              <div style={styles.bullet} />
              Check if the URL is typed correctly
            </div>
            <div style={styles.suggestionItem}>
              <div style={styles.bullet} />
              Go back to the previous page
            </div>
            <div style={styles.suggestionItem}>
              <div style={styles.bullet} />
              Return to the dashboard
            </div>
            <div style={styles.suggestionItem}>
              <div style={styles.bullet} />
              Use the search box below to find what you need
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div style={styles.buttonGroup}>
          <button
            style={styles.primaryButton}
            onClick={handleGoHome}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
            }}
          >
            <Home size={20} />
            Go to Dashboard
          </button>

          <button
            style={styles.secondaryButton}
            onClick={handleGoBack}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#667eea';
              e.currentTarget.style.color = '#ffffff';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = '#667eea';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <ArrowLeft size={20} />
            Go Back
          </button>
        </div>

        {/* Countdown */}
        <div style={styles.countdown}>
          Redirecting to dashboard in{' '}
          <span style={styles.countdownNumber}>{countdown}</span> seconds...
        </div>

        {/* Search Box */}
        <div style={styles.searchBox}>
          <form onSubmit={handleSearch} style={styles.searchForm}>
            <input
              type="text"
              placeholder="Search for pages, features, or content..."
              style={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            />
            <button type="submit" style={styles.searchButton}>
              <Search size={20} color="#667eea" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NotFound;